import type { Participant, Room, Track } from 'livekit-client';

/**
 * The video room behind one seam (E2E.md „Test doubles"): `getRoomClient()` returns `window.__brimoRoom()`
 * when a test injected that factory, otherwise the `livekit-client` implementation. Product code never
 * defines the factory. The UI only reads `participants()` and reacts to the three events.
 */

export interface BrimoRoomParticipant {
  identity: string;
  name: string;
  isLocal: boolean;
  speaking: boolean;
  audioTrack: MediaStreamTrack | null;
  videoTrack: MediaStreamTrack | null;
  screenTrack: MediaStreamTrack | null;
}

export type BrimoRoomEvent = 'participants' | 'activeSpeaker' | 'disconnected';

export interface BrimoRoomClient {
  readonly kind: 'livekit' | 'test';
  connect(input: { url: string; token: string }): Promise<void>;
  participants(): BrimoRoomParticipant[];
  on(event: BrimoRoomEvent, handler: () => void): () => void;
  /** `deviceId` picks the device chosen in the lobby (optional — a double may ignore it). */
  setMicrophone(on: boolean, deviceId?: string): Promise<void>;
  setCamera(on: boolean, deviceId?: string): Promise<void>;
  startScreenShare(): Promise<void>;
  stopScreenShare(): Promise<void>;
  disconnect(): Promise<void>;
}

export function getRoomClient(): BrimoRoomClient {
  const factory = typeof window === 'undefined' ? undefined : window.__brimoRoom;
  return factory ? factory() : new LiveKitRoomClient();
}

/** `livekit-client` (loaded on first connect): adaptive stream, dynacast, camera/microphone/screen share. */
class LiveKitRoomClient implements BrimoRoomClient {
  readonly kind = 'livekit' as const;
  private room: Room | null = null;
  private sources: typeof Track.Source | null = null;
  private readonly handlers = new Map<BrimoRoomEvent, Set<() => void>>();

  async connect(input: { url: string; token: string }): Promise<void> {
    const livekit = await import('livekit-client');
    const { Room: RoomClass, RoomEvent } = livekit;
    this.sources = livekit.Track.Source;
    const room = new RoomClass({ adaptiveStream: true, dynacast: true });
    const participants = () => this.emit('participants');
    for (const event of [
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.ParticipantNameChanged,
    ]) {
      room.on(event, participants);
    }
    room.on(RoomEvent.ActiveSpeakersChanged, () => {
      this.emit('activeSpeaker');
      this.emit('participants');
    });
    room.on(RoomEvent.Disconnected, () => this.emit('disconnected'));
    this.room = room;
    await room.connect(input.url, input.token);
    this.emit('participants');
  }

  participants(): BrimoRoomParticipant[] {
    const room = this.room;
    if (!room) return [];
    return [
      this.describe(room.localParticipant, true),
      ...[...room.remoteParticipants.values()].map((participant) => this.describe(participant, false)),
    ];
  }

  on(event: BrimoRoomEvent, handler: () => void): () => void {
    const set = this.handlers.get(event) ?? new Set<() => void>();
    set.add(handler);
    this.handlers.set(event, set);
    return () => {
      set.delete(handler);
    };
  }

  async setMicrophone(on: boolean, deviceId?: string): Promise<void> {
    await this.room?.localParticipant.setMicrophoneEnabled(on, deviceId ? { deviceId } : undefined);
    this.emit('participants');
  }

  async setCamera(on: boolean, deviceId?: string): Promise<void> {
    await this.room?.localParticipant.setCameraEnabled(on, deviceId ? { deviceId } : undefined);
    this.emit('participants');
  }

  async startScreenShare(): Promise<void> {
    await this.room?.localParticipant.setScreenShareEnabled(true, { audio: false });
    this.emit('participants');
  }

  async stopScreenShare(): Promise<void> {
    await this.room?.localParticipant.setScreenShareEnabled(false);
    this.emit('participants');
  }

  async disconnect(): Promise<void> {
    await this.room?.disconnect();
    this.room = null;
  }

  private emit(event: BrimoRoomEvent): void {
    for (const handler of this.handlers.get(event) ?? []) handler();
  }

  private describe(participant: Participant, isLocal: boolean): BrimoRoomParticipant {
    const sources = this.sources;
    const track = (source: Track.Source | undefined) => {
      if (source === undefined) return null;
      const publication = participant.getTrackPublication(source);
      if (!publication || publication.isMuted) return null;
      return publication.track?.mediaStreamTrack ?? null;
    };
    return {
      identity: participant.identity,
      name: participant.name || participant.identity,
      isLocal,
      speaking: participant.isSpeaking,
      audioTrack: track(sources?.Microphone),
      videoTrack: track(sources?.Camera),
      screenTrack: track(sources?.ScreenShare),
    };
  }
}
